/* eslint-disable react/no-unescaped-entities */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import React, { useCallback, useState } from "react";

import axios from "axios";

import { useMsal } from "@azure/msal-react";

import { b2cPolicies, deployment } from "../../../authConfig";
import { useEffect, useMemo } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_EditActionButtons,
  MRT_EditCellTextField,
  MRT_Row,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import * as Yup from "yup";
import { useFormik } from "formik";
import { findSigninAccount } from "../../../helpers/hooks/useAccountFinder";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf"; //or use your library of choice here
import autoTable from "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CSVLink, CSVDownload } from "react-csv";
import { RRM_EditCellTextField } from "../../../helpers/TextField";
import { ReactComponent as Export } from "../../../assets/export_notes.svg";
import { ReactComponent as MoreOption } from "../../../assets/more_vert.svg";
import { ReactComponent as Edit } from "../../../assets/open_in_new.svg";
import ViewAction from "../ViewAction";
import { RRM_EditActionButtons } from "../../../helpers/Button";

type IdTokenClaims = {
  roles: string[];
  idp: string;
  email: string;
  signInName: string;
  appTenantName: string;
  sub: string;
  appTenantId: string;
  QuestionId: string;
  acr: any;
};
//example data type
type HardwareRegister = {
  id: string;
  tenantId: string;
  deviceType: string;
  uniqueItemId: string;
  manager: string;
  assignee: string;
  inclusionDate: Date;
  deviceLocation: string;
  status: string;
  statusLastChanged: Date;
  suppliedVendorName: string;
  suppliedVendorContactEmail: string;
  suppliedVendorContactPhone: string;
  decomissionDate: Date;
  encrypted: string;
  protected: string;
  securelyErased: string;
  notes: string;
  lastUpdateDate: Date;
  isDeleted: boolean;
  otherDeviceType: string;
};
const extractedHeaders = {
  "Device Type": "deviceType",
  "Unique Item Id": "uniqueItemId",
  Manager: "manager",
  "User/Assignee": "assignee",
  "Date of Inclusion": "inclusionDate",
  "Device Location": "deviceLocation",
  Status: "status",
  "Status Last Changed": "statusLastChanged",
  "Supplier/Vendor Name": "suppliedVendorName",
  "Supplier/Vendor Contact Information": "suppliedVendorContactEmail",
  Encrypted: "encrypted",
  Protected: "protected",
  "Other Device Information": "notes",
};
interface FormErrors {
  deviceType?: string;
  uniqueItemId?: string;
  manager?: string;
  assignee?: string;
  inclusionDate?: Date;
  deviceLocation?: string;
  status?: string;
  statusLastChanged?: Date;
  suppliedVendorName?: string;
  suppliedVendorContactEmail?: string;
  suppliedVendorContactPhone?: string;
  decomissionDate?: Date;
  encrypted?: string;
  protected?: string;
  securelyErased?: string;
  notes?: string;
  lastUpdateDate?: Date;
  otherDeviceType?: string;
  // Add other fields as needed
}
const queryClient = new QueryClient();
const defaultStatus = [
  {
    label: "Allocated",
    value: "Allocated",
  },
  {
    label: "Pending Allocation",
    value: "Pending Allocation",
  },
  {
    label: "Decommissioned",
    value: "Decommissioned",
  },
  {
    label: "Lost",
    value: "Lost",
  },
];
const defaultDeviceType = [
  {
    label: "Laptop",
    value: "Laptop",
  },
  {
    label: "Desktop",
    value: "Desktop",
  },
  {
    label: "Tablet",
    value: "Tablet",
  },
  {
    label: "Mobile",
    value: "Mobile",
  },
  {
    label: "Other",
    value: "Other",
  },
];
const defaultDeviceLocation = [
  {
    label: "User",
    value: "User",
  },
  {
    label: "In Office",
    value: "In Office",
  },
  {
    label: "In Inventory",
    value: "In Inventory",
  },
  {
    label: "With Third Party Supplier",
    value: "With Third Party Supplier",
  },
];
const defaultEncrypted = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
  {
    label: "Not Sure",
    value: "Not Sure",
  },
];
const defaultProtected = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
  {
    label: "Not Sure",
    value: "Not Sure",
  },
];
const defaultSecurelyErased = [
  {
    label: "Yes",
    value: "Yes",
  },
  {
    label: "No",
    value: "No",
  },
];
const HardwareRegister = () => {
  //console.log("Members: " + props);

  const [hardwareRegisters, setHardwareRegisters] = useState<HardwareRegister[]>([]);
  const [data, setData] = useState<HardwareRegister[]>([]);
  const { instance } = useMsal();
  const accounts = findSigninAccount(instance.getAllAccounts());
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [users, setUsers] = React.useState<Member[]>([]);
  const [editDecomissionDate, setEditDecomissionDate] = React.useState(false);
  const [deviceType, setDeviceType] = React.useState("");
  const [deleteRowId, setDeleteRowId] = useState<string>("");
  const validateRequired = (value: string) => !!value.length;
  //validateDate if in future return false
  const validateDate = (value: Date) => {
    if (value === null || value === undefined) {
      return false;
    }
    return new Date(value).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
  };
  const validateDecommDate = (value: Date) => {
    if (value === null || value === undefined) {
      return false;
    }
    return true;
  };
  const validateEmail = (email: string) =>
    !!email.length &&
    email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );

  function validateItem(item: HardwareRegister) {
    return {
      deviceType: validateRequired(item.deviceType) ? undefined : "Device Type is required",
      otherDeviceType:
        item.deviceType === "Other"
          ? validateRequired(item.otherDeviceType)
            ? undefined
            : "Other Device Type Required"
          : undefined,
      uniqueItemId: validateRequired(item.uniqueItemId) ? undefined : "Unique Item Id is required",
      manager: validateRequired(item.manager) ? undefined : "Manager is required",

      assignee: !item.assignee
        ? validateRequired(item.assignee)
          ? undefined
          : "Assignee is required"
        : undefined,
      //inclusionDate: item.inclusionDate ? undefined : "Inclusion Date is required",
      inclusionDate: validateDate(item.inclusionDate)
        ? undefined
        : "Inclusion Date is required and cannot be in future",
      deviceLocation: validateRequired(item.deviceLocation)
        ? undefined
        : "Device Location is required",
      status: validateRequired(item.status) ? undefined : "Status is required",
      // statusLastChanged: item.statusLastChanged ? undefined : "Status Last Changed is required",
      /* suppliedVendorName: validateRequired(item.suppliedVendorName)
        ? undefined
        : "Supplied Vendor Name is required",
      suppliedVendorContactEmail: validateRequired(item.suppliedVendorContactEmail)
        ? undefined
        : "Supplied Vendor Contact Email is required", */

      decomissionDate:
        item.status === "Decommissioned"
          ? validateDecommDate(item.decomissionDate)
            ? undefined
            : "Decomission Date is required"
          : undefined,

      securelyErased:
        item.status === "Decommissioned"
          ? validateRequired(item.securelyErased)
            ? undefined
            : "Securely Erased is required"
          : undefined,

      encrypted: validateRequired(item.encrypted) ? undefined : "Encrypted is required",
      protected: validateRequired(item.protected) ? undefined : "Protected is required",
    };
  }
  const handleOrgClick = () => {
    window.location.href = "/get-started";
  };
  const getValueColor = (status: string) => {
    switch (status) {
      case "Yes":
        return "#4CAF50";
      case "No":
        return "#FF6666";
      case "Not Sure":
        return "#D4D4D4";

      default:
        return "transparent"; // Default color
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Allocated":
        return "#4CAF50";
      case "Pending Allocation":
        return "#FF9900";
      case "Lost":
        return "#D4D4D4";
      case "Decommissioned":
        return "#FF6666";

      default:
        return "transparent"; // Default color
    }
  };
  const columns = useMemo<MRT_ColumnDef<HardwareRegister>[]>(
    () => [
      {
        accessorKey: "id", //access nested data with dot notation
        header: "System Id",
        enableEditing: false,
        visibleInShowHideMenu: false,
      },
      // added on
      {
        accessorKey: "inclusionDate", //access nested data with dot notation
        header: "Added on",
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.inclusionDate,
          helperText: validationErrors?.inclusionDate,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              inclusionDate: undefined,
            }),
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === null || cell.getValue() === undefined) {
            return "";
          }
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        enableEditing: true,
        size: 5,
      },
      // uniqueItemId
      {
        accessorKey: "uniqueItemId", //access nested data with dot notation
        header: "Unique Item Id",

        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              // width: "60vh",
              whiteSpace: "pre-wrap",
              cursor: "pointer", // Add cursor pointer to indicate it's clickable
              textDecoration: "underline", // Add underline to indicate it's a hyperlink
              // color: "black", // Add color to indicate it's a hyperlink
            }}
            onClick={() => setSelectedViewRowId(row.original.id)}
          >
            <strong color="#111827">{row.original.uniqueItemId}</strong>
            {/* <span style={{ fontSize: "14px", color: "#66696C" }}>{row.original.systemSummary}</span> */}
          </Box>
        ),
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.uniqueItemId,
          helperText: validationErrors?.uniqueItemId,
          //remove any previous validation errors when user focuses on the input

          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              uniqueItemId: undefined,
            }),
          onBlur: (e) => {
            console.log("onBlur", e.target.value);
            // check if systemName irrespective of case exists in fetchedItems , if yes then show error
            if (
              fetchedItems.some((item) => {
                return item.uniqueItemId.toUpperCase() === e.target.value.toUpperCase();
              })
            ) {
              setValidationErrors({
                ...validationErrors,
                uniqueItemId: "Item Id already exists",
              });
            }
          },

          //optionally add validation checking for onBlur or onChange
        },
        enableEditing: (row) => {
          return row.original.id === "";
        },
      },
      // deviceType
      {
        accessorKey: "deviceType", //access nested data with dot notation
        header: "Device Type",
        editVariant: "select",
        editSelectOptions: defaultDeviceType,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.deviceType,
          helperText: validationErrors?.deviceType,
          onChange: (e) => {
            setDeviceType(e.target.value);
          },
        },
        enableEditing: true,
      },
      // assignee
      {
        accessorKey: "assignee", //access nested data with dot notation
        header: "User",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.assignee,
          helperText: validationErrors?.assignee,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              assignee: undefined,
            }),
        },
        enableEditing: true,
      },
      // status
      {
        accessorKey: "status", //access nested data with dot notation
        header: "Status",
        editVariant: "select",
        editSelectOptions: defaultStatus,
        muiEditTextFieldProps: ({ cell, row, table }) => ({
          onLoad: () => {
            console.log("ON load status", cell.getValue());
            if (cell.getValue() === "Decommissioned") {
              setEditDecomissionDate(true);
            } else {
              setEditDecomissionDate(false);
            }
          },
          onChange: (event) => {
            if (event.target.value === "Decommissioned") {
              setEditDecomissionDate(true);
            } else {
              setEditDecomissionDate(false);
            }
          },
          select: true,
          required: true,
          error: !!validationErrors?.status,
          helperText: validationErrors?.status,
        }),
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginLeft: "15px",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "12px",
                height: "12px", // Adjust height as needed
                backgroundColor: getStatusColor(row.original.status),
                borderRadius: "50%",
                // transform: "translateY(-50%)",
                left: "-15px", // Adjust position of the line
              }}
            />
            <span>{row.original.status}</span>
          </div>
        ),
      },
      // suppliedVendorName
      {
        accessorKey: "suppliedVendorName", //access nested data with dot notation
        header: "Supplier/Vendor Name",
        muiEditTextFieldProps: {
          type: "text",
          //required: true,
          error: !!validationErrors?.suppliedVendorName,
          helperText: validationErrors?.suppliedVendorName,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              suppliedVendorName: undefined,
            }),
        },
        enableEditing: true,
      },
      //   encrypted
      {
        accessorKey: "encrypted", //access nested data with dot notation
        header: "Encrypted",
        editVariant: "select",
        editSelectOptions: defaultEncrypted,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.encrypted,
          helperText: validationErrors?.encrypted,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.encrypted),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.encrypted}</span>
          </div>
        ),
        size: 10,
      },
      // protected
      {
        accessorKey: "protected", //access nested data with dot notation
        header: "Protected",
        editVariant: "select",
        editSelectOptions: defaultProtected,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.protected,
          helperText: validationErrors?.protected,
        },
        enableEditing: true,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ position: "relative", display: "inline-block" }}>
            <div
              style={{
                position: "absolute",
                width: "5px",
                height: "100px", // Adjust height as needed
                backgroundColor: getValueColor(row.original.protected),
                top: "50%",
                transform: "translateY(-50%)",
                left: "-18px", // Adjust position of the line
              }}
            />
            <span>{row.original.protected}</span>
          </div>
        ),
        size: 10,
      },
      // otherDeviceType
      {
        accessorKey: "otherDeviceType", //access nested data with dot notation
        header: "Other Device Type",
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.otherDeviceType,
          helperText: validationErrors?.otherDeviceType,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              otherDeviceType: undefined,
            }),
          //optionally add validation checking for onBlur or onChange
        },
        enableEditing: true,
      },

      // manager
      {
        accessorKey: "manager", //access nested data with dot notation
        header: "Manager",
        editVariant: "select",
        editSelectOptions: users
          ?.filter(
            (user) =>
              user.status === "Active" &&
              (user.roles.includes("Tenant.admin") || user.roles.includes("Tenant.manager"))
          )
          .map((user) => ({
            label: user.givenName + " " + user.surname,
            value: user.id,
          })),
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.manager,
          helperText: validationErrors?.manager,
        },
        Cell: ({ renderedCellValue, row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <span>
              {row.original.manager
                ? users?.find((user) => user.id === row.original.manager)?.givenName +
                  " " +
                  users?.find((user) => user.id === row.original.manager)?.surname
                : ""}
            </span>
          </Box>
        ),
        enableEditing: true,
      },
      // deviceLocation
      {
        accessorKey: "deviceLocation", //access nested data with dot notation
        header: "Device Location",
        editVariant: "select",
        editSelectOptions: defaultDeviceLocation,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.deviceLocation,
          helperText: validationErrors?.deviceLocation,
        },
        enableEditing: true,
      },
      // statusLastChanged
      {
        accessorKey: "statusLastChanged", //access nested data with dot notation
        header: "Status Last Changed Date",
        muiEditTextFieldProps: {
          type: "date",
          //required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.statusLastChanged,
          helperText: validationErrors?.statusLastChanged,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              statusLastChanged: undefined,
            }),
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === "") {
            return "";
          }
          console.log("cell.getValue()", cell.getValue<Date>());
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        enableEditing: false,
      },

      // suppliedVendorContactEmail
      {
        accessorKey: "suppliedVendorContactEmail", //access nested data with dot notation
        header: "Supplier/Vendor Contact Information",
        muiEditTextFieldProps: {
          type: "text",
          //required: true,
          error: !!validationErrors?.suppliedVendorContactEmail,
          helperText: validationErrors?.suppliedVendorContactEmail,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              suppliedVendorContactEmail: undefined,
            }),
        },
        enableEditing: true,
      },

      // decomissionDate
      {
        accessorKey: "decomissionDate", //access nested data with dot notation
        header: "Decommission Date",
        muiEditTextFieldProps: {
          type: "date",
          required: true,
          InputLabelProps: { shrink: true },
          error: !!validationErrors?.decomissionDate,
          helperText: validationErrors?.decomissionDate,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              decomissionDate: undefined,
            }),
        },
        Cell: ({ cell }) => {
          if (cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === "") {
            return "";
          }
          return new Date(cell.getValue<Date>()).toLocaleDateString();
        }, //render Date as a string
        //enableEditing: (row) => row._valuesCache.status === "Decommissioned",
      },
      // securelyErased
      {
        accessorKey: "securelyErased", //access nested data with dot notation
        header: "Securely Erased",
        editVariant: "select",
        editSelectOptions: defaultSecurelyErased,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.securelyErased,
          helperText: validationErrors?.securelyErased,
        },
        enableEditing: (row) => row._valuesCache.status === "Decommissioned",
      },
      // notes
      {
        accessorKey: "notes", //access nested data with dot notation
        header: "Other Device Information",
        muiEditTextFieldProps: {
          type: "text",
          error: !!validationErrors?.notes,
          helperText: validationErrors?.notes,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              notes: undefined,
            }),
        },
        enableEditing: true,
      },
    ],
    [validationErrors, users]
  );
  const handleExportRows = (rows: any) => {
    const doc = new jsPDF();
    //derive column headers and accesorKey from the columns array

    //create a new structure from columns accessorKey
    const colStruct = [
      { accessorKey: "deviceType", header: "Device Type" },
      { accessorKey: "uniqueItemId", header: "Unique Item Id" },
      { accessorKey: "manager", header: "Manager" },
      { accessorKey: "status", header: "Status" },
      { accessorKey: "inclusionDate", header: "Inclusion Date" },
    ];
    //from colStruct get the headers but use columns structure
    const tableHeaders = colStruct.map((col) => col.header);
    //const tableHeaders = columns.map((column) => column.header);
    //from rows.original get the values for each column using colStruct
    const tableData = rows.map((row: any) =>
      colStruct.map((col) => {
        if (col.accessorKey === "inclusionDate") {
          return new Date(row.original[col.accessorKey]).toLocaleDateString("en-AU");
        }
        if (col.accessorKey === "manager") {
          return (
            users?.find((user) => user.id === row.original[col.accessorKey])?.givenName +
            " " +
            users?.find((user) => user.id === row.original[col.accessorKey])?.surname
          );
        }
        return row.original[col.accessorKey];
      })
    );

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
    });

    doc.save("detail-systems.pdf");
  };
  //READ hook (get users from api)
  function useGetItems() {
    return useQuery<HardwareRegister[]>({
      queryKey: ["hardwareregister"],
      queryFn: async () => {
        const appTenantId = (accounts[0]?.idTokenClaims as IdTokenClaims)?.appTenantId;
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_SERVER}/rm/user/getAllUsersForTenant/` + appTenantId
        );
        setUsers((prevValue: Member[]) => [...prevValue, ...userResponse.data]);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/getAllHardwareRegisterForTenant/` +
              appTenantId
          );
          setData(response.data);
          return response.data;
        } catch (error: any) {
          // Handle 404 error (Not Found)
          if (error.response && error.response.status === 404) {
            console.error("Data not found:", error.message);
            return []; // Return an empty array or handle as needed
          }

          // Handle other errors
          console.error("Error fetching data:", error.message);
          throw error; // Re-throw the error for React Query to handle
        }
      },
      refetchOnWindowFocus: false,
    });
  }

  function useCreateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: HardwareRegister) => {
        //send api update request here
        await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: async (newItemInfo: HardwareRegister) => {
        console.log("firing create", newItemInfo);
        let response = await axios.post(
          `${process.env.REACT_APP_API_SERVER}/rm/assetregister/createHardwareRegister`,
          {
            tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
            deviceType: newItemInfo.deviceType,
            uniqueItemId: newItemInfo.uniqueItemId,
            manager: newItemInfo.manager,
            assignee: newItemInfo.assignee,
            inclusionDate: newItemInfo.inclusionDate,
            deviceLocation: newItemInfo.deviceLocation,
            status: newItemInfo.status,
            statusLastChanged: new Date(),
            suppliedVendorName: newItemInfo.suppliedVendorName,
            suppliedVendorContactEmail: newItemInfo.suppliedVendorContactEmail,
            suppliedVendorContactPhone: newItemInfo.suppliedVendorContactPhone,
            decomissionDate: newItemInfo.decomissionDate,
            securelyErased: newItemInfo.securelyErased,
            notes: newItemInfo.notes,
            encrypted: newItemInfo.encrypted,
            protected: newItemInfo.protected,
            otherDeviceType: newItemInfo.otherDeviceType,
          }
        );
        if (response.status !== 201) {
          alert("Error creating system");
          return;
        }

        let itemId = (await response).data.id;

        queryClient.setQueryData(
          ["hardwareregister"],
          (prevItems: HardwareRegister[] | undefined) => {
            const newItems = [
              ...(prevItems || []), // Use an empty array if prevItems is undefined or not iterable
              {
                ...newItemInfo,
                id: itemId,
                statusLastChanged: new Date(),
              },
            ];
            return newItems as HardwareRegister[];
          }
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //UPDATE action
  const handleSaveItem: MRT_TableOptions<HardwareRegister>["onEditingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    console.log("values", values);
    await updateItem(values);
    table.setEditingRow(null); //exit editing mode
  };
  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<HardwareRegister>) => {
    deleteItem(row.original.id);
    handleClose();
  };
  // delete action
  const [anchordEl, setAnchordEl] = React.useState<null | HTMLElement>(null);
  const deleteOpen = Boolean(anchordEl);

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchordEl(event.currentTarget);
  };

  const handleDeleteClose = () => {
    setAnchordEl(null);
  };
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedRowObject(null);
    setSelectedViewRowId("");
    setDeleteRowId("");
  };
  //DELETE action
  // const openDeleteConfirmModal = (row: MRT_Row<HardwareRegister>) => {
  //   if (window.confirm("Are you sure you want to delete this device?")) {
  //     deleteItem(row.original.id);
  //   }
  // };

  //UPDATE hook (put user in api)
  function useUpdateItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (item: HardwareRegister) => {
        console.log("item in update", item);
        //send api update request here
        await new Promise<void>((resolve) => {
          let response = axios.put(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/saveHardwareRegister`,
            {
              tenantId: (accounts[0].idTokenClaims as IdTokenClaims).appTenantId,
              deviceType: item.deviceType,
              uniqueItemId: item.uniqueItemId,
              manager: item.manager,
              assignee: item.assignee,
              inclusionDate: item.inclusionDate,
              deviceLocation: item.deviceLocation,
              status: item.status,
              statusLastChanged: new Date(),
              suppliedVendorName: item.suppliedVendorName,
              suppliedVendorContactEmail: item.suppliedVendorContactEmail,
              suppliedVendorContactPhone: item.suppliedVendorContactPhone,
              decomissionDate: item.decomissionDate,
              encrypted: item.encrypted,
              protected: item.protected,
              securelyErased: item.securelyErased,
              notes: item.notes,
              id: item.id,
              otherDeviceType: item.otherDeviceType,
            }
          );
          console.log(response);
          resolve(); // Resolve the outer promise
        }); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: (newItemInfo: HardwareRegister) => {
        console.log("firing update", newItemInfo);
        newItemInfo.statusLastChanged = new Date();
        queryClient.setQueryData(["hardwareregister"], (prevUsers: any) =>
          prevUsers?.map((prevUser: HardwareRegister) =>
            prevUser.id === newItemInfo.id ? newItemInfo : prevUser
          )
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //DELETE hook (delete user in api)
  function useDeleteItem() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (id: string) => {
        //send api update request here
        await new Promise<void>((resolve) => {
          let response = axios.delete(
            `${process.env.REACT_APP_API_SERVER}/rm/assetregister/deleteHardwareRegister?tenantId=${
              (accounts[0].idTokenClaims as IdTokenClaims).appTenantId
            }&id=${id}`
          );
          console.log(response);
          resolve(); // Resolve the outer promise
        }); //fake api call
        return Promise.resolve();
      },
      //client side optimistic update
      onMutate: (id: string) => {
        queryClient.setQueryData(["hardwareregister"], (prevUsers: any) =>
          prevUsers?.filter((item: HardwareRegister) => item.id !== id)
        );
      },
      // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
  }
  //call CREATE hook
  const { mutateAsync: createItem, isPending: isCreatingItem } = useCreateItem();
  //call READ hook
  const {
    data: fetchedItems = [],
    isError: isLoadingItemsError,
    isFetching: isFetchingItems,
    isLoading: isLoadingITems,
  } = useGetItems();
  const headers = Object.keys(extractedHeaders);

  // Convert data to the required format (array of arrays) with dynamic column mapping
  const convertedData = [
    headers,
    ...fetchedItems.map((record: HardwareRegister) =>
      headers.map((header: string) => {
        const key = extractedHeaders[header as keyof typeof extractedHeaders];
        if (key === "manager") {
          if (
            record[key as keyof HardwareRegister] === null ||
            record[key as keyof HardwareRegister] === undefined
          )
            return "";
          return (
            users?.find((user) => user.id === (record[key as keyof HardwareRegister] as string))
              ?.givenName +
            " " +
            users?.find((user) => user.id === (record[key as keyof HardwareRegister] as string))
              ?.surname
          );
        } else if (key === "statusLastChanged") {
          return ((new Date(
            record[key as keyof HardwareRegister] as string
          ).toLocaleDateString() as string) +
            " " +
            new Date(
              record[key as keyof HardwareRegister] as string
            ).toLocaleTimeString()) as string;
        } else {
          return record[key as keyof HardwareRegister] as string;
        }
      })
    ),
  ];

  //call UPDATE hook
  const { mutateAsync: updateItem, isPending: isUpdatingItem } = useUpdateItem();
  //call DELETE hook
  const { mutateAsync: deleteItem, isPending: isDeletingItem } = useDeleteItem();
  //CREATE action
  const handleCreateItem: MRT_TableOptions<HardwareRegister>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    const newValidationErrors = validateItem(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createItem(values);
    table.setCreatingRow(null); //exit creating mode
  };
  const table = useMaterialReactTable({
    columns,
    data: fetchedItems, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    createDisplayMode: "modal", //default ('row', and 'custom' are also available)
    editDisplayMode: "modal", //default ('row', 'cell', 'table', and 'custom' are also available)
    enableEditing: true,
    positionActionsColumn: "last",
    enableTopToolbar: false,

    getRowId: (row) => row.id,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
      boxShadow: "none !important",
      borderRadius: "none !important",
    }),
    muiTableBodyRowProps: {
      hover: false,
      sx: {
        whiteSpace: "nowrap",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none !important",
        // width: "1187px",
        // minWidth: "1187px",
      },
    },
    muiTableProps: {
      sx: {
        // border: "1px solid rgba(81, 81, 81, .5)",
        boxShadow: "none !important",
        whiteSpace: "nowrap",
        borderRadius: "none !important",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #EDEDED",
        // borderLeft: "1px solid rgba(81, 81, 81, .5)",
        // borderTop: "1px solid rgba(81, 81, 81, .5)",
        // fontStyle: "italic",
        fontWeight: "bold",
        fontSize: "14px",
        whiteSpace: "nowrap",
        textAlign: "center",
        boxShadow: "none",
        paddingLeft: "20px",
        backgroundColor: "#FBFBFF",
        color: "#111827 60%",
        height: "50px",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: "1px solid rgba(81, 81, 81, .5)",
        // whiteSpace: "pre-wrap",
        // paddingLeft: "20px",
        // paddingRight: "20px",
        paddingTop: "10px",
        paddingBottom: "10px",
        border: "1px solid #EDEDED",
        // borderLeft: columns.accessorKey === "status" ? "none" : "1px solid #EDEDED",
      },
    },
    muiToolbarAlertBannerProps: isLoadingItemsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    muiTableContainerProps: {
      sx: {
        minHeight: "100px",
      },
    },
    // renderTopToolbarCustomActions: ({ table }) => (
    //   <>
    //     <Button
    //       variant="contained"
    //       onClick={() => {
    //         // handleAddClick();
    //         table.setCreatingRow(true); //simplest way to open the create row modal with no default values
    //         //or you can pass in a row object to set default values with the `createRow` helper function
    //         // table.setCreatingRow(
    //         //   createRow(table, {
    //         //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
    //         //   }),
    //         // );
    //       }}
    //     >
    //       Add Hardware
    //     </Button>
    //   </>
    // ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateItem,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveItem,
    //optionally customize modal content
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => {
      return (
        <>
          <Dialog
            open={true}
            onClose={() => table.setEditingRow(null)}
            fullWidth={true}
            maxWidth={"lg"}
          >
            <DialogTitle variant="h3">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="16px" fontWeight="600">
                  Edit Device/Hardware
                </Typography>
                <RRM_EditActionButtons variant="icon" table={table} row={row} />
              </Box>
              <Divider sx={{ marginTop: "1rem" }} />
            </DialogTitle>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="id"
                  table={table}
                  disabled={true}
                  style={{ display: "none" }} //still have to put edit field else id is not sent in update
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="deviceType"
                  table={table}
                  style={{}}
                  // sx={{ width: 200 }}
                />
                {deviceType === "Other" ? (
                  <RRM_EditCellTextField row={row} accessKey="otherDeviceType" table={table} />
                ) : (
                  <RRM_EditCellTextField
                    row={row}
                    accessKey="otherDeviceType"
                    table={table}
                    disabled={true}
                  />
                )}

                <RRM_EditCellTextField
                  row={row}
                  accessKey="uniqueItemId"
                  table={table}
                  tooltip="Identifiers should be unique within the Organisation, e.g. by incorporating the device's serial number into the identifier."
                  inputProps={{ maxLength: 100 }}
                />
                <RRM_EditCellTextField row={row} accessKey="manager" table={table} style={{}} />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="assignee"
                  labelName="User/Assignee"
                  table={table}
                  style={{}}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="inclusionDate"
                  labelName="Date of Inclusion"
                  table={table}
                  style={{}}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="deviceLocation"
                  table={table}
                  style={{}}
                />
                <RRM_EditCellTextField row={row} accessKey="status" table={table} style={{}} />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                {/* <RRM_EditCellTextField
                  row={row}
                  accessKey="statusLastChanged"
                  table={table}
                  style={{}}
                /> */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                    gap: "5px",
                    width: "765px",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    color="rgba(17, 24, 39, 0.6)"
                    fontWeight="600"
                    sx={{ marginLeft: "5px" }}
                  >
                    Status Last Changed
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    margin="none"
                    size="small"
                    // label="Last Status Changed"
                    value={new Date(row.original.statusLastChanged).toLocaleDateString()}
                    disabled
                  />
                </Box>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="suppliedVendorName"
                  table={table}
                  style={{}}
                  inputProps={{ maxLength: 100 }}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="suppliedVendorContactEmail"
                  table={table}
                  style={{}}
                  inputProps={{
                    maxLength: 1000,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="decomissionDate"
                  table={table}
                  style={{}}
                  disabled={!editDecomissionDate}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="securelyErased"
                  table={table}
                  style={{}}
                  disabled={!editDecomissionDate}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="encrypted"
                  tooltip=" Has the data on the device been encrypted? (e.g. using BitLocker for Windows devices)"
                  table={table}
                  style={{}}
                />
                <RRM_EditCellTextField
                  row={row}
                  accessKey="protected"
                  tooltip="Is the device protected, e.g. with antivirus software, automatic screen locking or  password protection? Refer to Workflow Manager for examples of appropriate actions."
                  table={table}
                  style={{}}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <RRM_EditCellTextField
                  row={row}
                  accessKey="notes"
                  table={table}
                  style={{}}
                  multiline
                />
              </Box>

              {/* or render custom edit components here */}
            </DialogContent>
            {/* <DialogActions>
              <MRT_EditActionButtons variant="text" table={table} row={row} />
            </DialogActions> */}
          </Dialog>
        </>
      );
    },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <>
        <Dialog
          open={true}
          onClose={() => table.setEditingRow(null)}
          fullWidth={true}
          maxWidth={"lg"}
        >
          {" "}
          <DialogTitle variant="h3">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography fontSize="16px" fontWeight="600">
                Add Device/Hardware
              </Typography>
              <RRM_EditActionButtons variant="icon" table={table} row={row} />
            </Box>
            <Divider sx={{ marginTop: "1rem" }} />
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}
          >
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="deviceType"
                table={table}
                style={{}}
                // sx={{ width: 200 }}
              />
              {/* {(deviceType === "Other" && (
                <RRM_EditCellTextField row={row} accessKey="otherDeviceType" table={table} />
              )) || <></>} */}
              {deviceType === "Other" ? (
                <RRM_EditCellTextField row={row} accessKey="otherDeviceType" table={table} />
              ) : (
                <RRM_EditCellTextField
                  row={row}
                  accessKey="otherDeviceType"
                  table={table}
                  disabled={true}
                />
              )}

              <RRM_EditCellTextField
                row={row}
                accessKey="uniqueItemId"
                table={table}
                tooltip="Identifiers should be unique within the Organisation, e.g. by incorporating the device's serial number into the identifier."
                inputProps={{
                  maxLength: 100,
                }}
              />
              <RRM_EditCellTextField row={row} accessKey="manager" table={table} style={{}} />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="assignee"
                labelName="User/Assignee"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="inclusionDate"
                labelName="Date of Inclusion"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="deviceLocation"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField row={row} accessKey="status" table={table} style={{}} />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="statusLastChanged"
                labelName="Status Last Changed"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="suppliedVendorName"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="suppliedVendorContactEmail"
                table={table}
                style={{}}
                inputProps={{ maxLength: 1000 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="decomissionDate"
                table={table}
                style={{}}
                disabled={!editDecomissionDate}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="securelyErased"
                table={table}
                style={{}}
                disabled={!editDecomissionDate}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="encrypted"
                tooltip=" Has the data on the device been encrypted? (e.g. using BitLocker for Windows devices)"
                table={table}
                style={{}}
              />
              <RRM_EditCellTextField
                row={row}
                accessKey="protected"
                tooltip="Is the device protected, e.g. with antivirus software, automatic screen locking or  password protection? Refer to Workflow Manager for examples of appropriate actions."
                table={table}
                style={{}}
              />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <RRM_EditCellTextField
                row={row}
                accessKey="notes"
                table={table}
                style={{}}
                multiline
              />
            </Box>
          </DialogContent>
          {/* <DialogActions>
            <MRT_EditActionButtons variant="text" table={table} row={row} />
          </DialogActions> */}
        </Dialog>
      </>
    ),
    // renderToolbarInternalActions: ({ table }) => (
    //   <Box sx={{ display: "flex", gap: "20px", alignItems: "center", paddingRight: "10px" }}>
    //     <Button
    //       variant="contained"
    //       onClick={() => {
    //         table.setCreatingRow(true); //simplest way to open the create row modal with no default values
    //       }}
    //     >
    //       Add Hardware
    //     </Button>
    //     <Button startIcon={<Export />}>
    //       <CSVLink data={convertedData} filename={"Hardware Device.csv"}>
    //         Export Data
    //       </CSVLink>
    //     </Button>
    //   </Box>
    // ),
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "2px" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            sx={{ padding: "2px" }}
            onClick={(e) => {
              setDeleteRowId(row.original.id);
              handleDeleteClick(e);
            }}
          >
            <MoreOption />
          </IconButton>
        </Tooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchordEl}
          open={deleteOpen}
          onClose={() => handleDeleteClose()}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => {
              // setDeleteRowId(row.original.id);
              // setConfirmOpen(true);
              handleDeleteClose();
              handleOpen();
            }}
          >
            Delete
          </MenuItem>
        </Menu>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography fontSize="17px" fontWeight="bold" id="modal-modal-title">
              Are you sure you want to delete this device?
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "2rem",
                mt: "1rem",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ color: "white", backgroundColor: "red" }}
                onClick={() => {
                  deleteItem(deleteRowId);
                  handleClose();
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    ),
    enableHiding: false,
    state: {
      isLoading: isLoadingITems,
      isSaving: isCreatingItem || isUpdatingItem || isDeletingItem,
      showAlertBanner: isLoadingItemsError,
      showProgressBars: isFetchingItems,
    },
    enableColumnActions: false,

    initialState: {
      //optional initial state
      columnVisibility: {
        id: false,
        otherDeviceType: false,
        manager: false,
        deviceLocation: false,
        statusLastChanged: false,
        suppliedVendorContactEmail: false,
        decomissionDate: false,
        securelyErased: false,
        notes: false,
      },
    },
    defaultColumn: {
      minSize: 10, //allow columns to get smaller than default
      maxSize: 250, //allow columns to get larger than default
      size: 50, //make columns wider by default
    },
  });

  const [selectedViewRowId, setSelectedViewRowId] = useState<string>("");
  const [selectedRowObject, setSelectedRowObject] = useState<HardwareRegister | null>(null); // Specify type explicitly

  // Effect to update selectedRowObject when selectedViewRowId changes
  useEffect(() => {
    if (selectedViewRowId !== "") {
      // Find the object in data array with the matching id
      const selectedObject = fetchedItems.find((obj) => obj.id === selectedViewRowId) || null; // Provide default value of null
      setSelectedRowObject(selectedObject);
    } else {
      setSelectedRowObject(null); // Reset selectedRowObject if selectedViewRowId is empty
    }
  }, [selectedViewRowId, fetchedItems]);

  return (
    <>
      {data ? (
        <div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              alignItems: "center",
              paddingRight: "10px",
              position: "relative",
              top: "-35px",
              // width: "826px",
              // left: "68vh",
              marginLeft: "428px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                table.setCreatingRow(true);
              }}
            >
              Add Hardware
            </Button>
            <Button startIcon={<Export />}>
              <CSVLink data={convertedData} filename={"Hardware Device.csv"}>
                <Typography fontSize="14px" color="#233DFF" fontWeight="bold">
                  Export Data
                </Typography>
              </CSVLink>
            </Button>
          </Box>
          <MaterialReactTable table={table} />
          {selectedViewRowId !== "" && (
            <ViewAction action={selectedRowObject} type="hardware" onClose={handleClose} />
          )}
        </div>
      ) : (
        <p>Loading, please wait...</p>
      )}
    </>
  );
};

const HardwareRegisterWithProviders = () => (
  //Put this with your other react-query providers near root of your app
  <QueryClientProvider client={queryClient}>
    <HardwareRegister />
  </QueryClientProvider>
);
export default HardwareRegisterWithProviders;
